import {Box, Typography} from '@mui/material';
import {Fragment, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {useNavigate, useParams} from 'react-router-dom';
import {ReactComponent as DeclineIcon} from '../../assets/icons/decline.svg';
import {ReactComponent as DeleteIcon} from '../../assets/icons/delete.svg';
import {ReactComponent as FilledFlagIcon} from '../../assets/icons/filled-flag.svg';
import {ReactComponent as FlagIcon} from '../../assets/icons/flag.svg';
import useUser from '../../hooks/common/use-user';
import Notification from '../../models/entities/notification';
import paths from '../../routes/paths';
import services from '../../services/provider';
import useBreadcrumbsStore from '../../state/breadcrumbs';
import notificationUtils from '../../utils/notifications';
import stringUtils from '../../utils/strings';
import IconButton from '../common/IconButton';
import LoadingBackdrop from '../common/LoadingBackdrop';
import Menu from '../common/Menu';
import Span from '../common/Span';
import SwitchInverted from '../common/SwitchInverted';
import NotificationList from './NotificationList';

function getTabIndex(tab?: string) {
  switch (tab) {
    case 'maintenance':
      return 1;
    case 'flagged':
      return 2;
    case 'warnings':
    default:
      return 0;
  }
}

function Notifications() {
  const navigate = useNavigate();
  const {tab, notificationId} = useParams();
  const {t} = useTranslation();
  const queryClient = useQueryClient();

  const {user} = useUser();

  const setBreadcrumbs = useBreadcrumbsStore((state) => state.setBreadcrumbs);

  const [isSelectingMultiple, setIsSelectingMultiple] = useState(false);
  const [selectedNotificationIds, setSelectedNotificationIds] = useState<string[]>([]);

  const tabs = [
    {value: 'warnings', label: t('profile_notifications_tabs_warnings_label')},
    {value: 'maintenance', label: t('profile_notifications_tabs_maintenance_label')},
    {value: 'flagged', label: t('profile_notifications_tabs_flagged_label')},
  ];
  const selectedTabIndex = getTabIndex(tab);
  const selectedTab = tabs[selectedTabIndex];

  useEffect(() => {
    setBreadcrumbs([
      {
        title: t('profile_breadcrumb'),
        onClick: () => navigate(paths.profile),
      },
      {
        title: t('profile_notifications_breadcrumb'),
        onClick: () => {
          setIsSelectingMultiple(false);
          setSelectedNotificationIds([]);
          navigate(`${paths.profileNotifications}/warnings`);
        },
      },
      {title: selectedTab.label},
    ]);
    return () => setBreadcrumbs([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  const {data: notifications = [], isLoading: isLoadingNotifications} = useQuery({
    enabled: user != null,
    queryKey: ['notifications'],
    queryFn: () =>
      services.notification.getNotifications({
        query: {userId: user?.id, companyId: user?.companyId},
      }),
  });

  const {mutate: updateNotification} = useMutation({
    mutationFn: services.notification.updateNotification,
    onMutate: async ({request: updatedNotification}) => {
      await queryClient.cancelQueries('notifications');
      const previousNotifications = queryClient.getQueryData<Notification[]>(['notifications']);
      queryClient.setQueryData<Notification[] | undefined>(['notifications'], (notifications) =>
        notifications?.map((notification) =>
          notification.id === updatedNotification.id ? updatedNotification : notification,
        ),
      );
      return {previousNotifications};
    },
    onError: (error, request, context) => {
      queryClient.setQueryData(['notifications'], context?.previousNotifications);
    },
    onSettled: () => {
      queryClient.invalidateQueries('notifications');
    },
  });

  const {mutate: deleteNotification, isLoading: isLoadingDeleteNotification} = useMutation({
    mutationFn: services.notification.deleteNotification,
    onSuccess: (notificationId) => {
      queryClient.setQueryData<Notification[] | undefined>(['notifications'], (notifications) =>
        notifications?.filter((notification) => notification.id !== notificationId),
      );
      queryClient.invalidateQueries('notifications');
    },
  });

  const {mutate: deleteNotifications, isLoading: isLoadingDeleteNotifications} = useMutation({
    mutationFn: services.notification.deleteNotifications,
    onSuccess: (notificationIds) => {
      queryClient.setQueryData<Notification[] | undefined>(['notifications'], (notifications) =>
        notifications?.filter((notification) => !notificationIds.includes(notification.id)),
      );
      queryClient.invalidateQueries('notifications');
    },
  });

  const totals = useMemo(() => {
    let unseenWarnings = 0;
    let unseenMaintenance = 0;
    let flagged = 0;
    notifications?.forEach((notification) => {
      if (notificationUtils.isWarning(notification) && !notification.isSeen) {
        unseenWarnings++;
      }
      if (notificationUtils.isMaintenance(notification) && !notification.isSeen) {
        unseenMaintenance++;
      }
      if (notification.isFlagged) {
        flagged++;
      }
    });
    return {unseenWarnings, unseenMaintenance, flagged};
  }, [notifications]);

  const sortedNotifications = useMemo(
    () =>
      Array.from(notifications ?? []).sort((notificationA, notificationB) => {
        if (notificationA.isSeen === notificationB.isSeen) {
          return (
            new Date(notificationB.createdAt).getTime() -
            new Date(notificationA.createdAt).getTime()
          );
        }
        return notificationA.isSeen ? 1 : -1;
      }),
    [notifications],
  );

  const selectedNotification = notifications?.find(
    (notification) => notification.id === notificationId,
  );

  useEffect(() => {
    const notification = notifications.find((notification) => notification.id === notificationId);

    if (notification != null) {
      updateNotification({
        params: {notificationId: notification.id},
        request: {...notification, isSeen: true},
      });
    }
  }, [notificationId, notifications, updateNotification]);

  const filteredNotifications = sortedNotifications.filter(
    (notification) =>
      (selectedTabIndex === 0 && notificationUtils.isWarning(notification)) ||
      (selectedTabIndex === 1 && notificationUtils.isMaintenance(notification)) ||
      (selectedTabIndex === 2 && notification.isFlagged),
  );

  function handleChangeSelectedTab(tabIndex: number) {
    setIsSelectingMultiple(false);
    setSelectedNotificationIds([]);
    navigate(`${paths.profileNotifications}/${tabs[tabIndex].value}`);
  }

  function handleSelectNotification(notificationId: string) {
    setIsSelectingMultiple(false);
    setSelectedNotificationIds([]);
    navigate(`${paths.profileNotifications}/${selectedTab.value}/${notificationId}`);
  }

  function handleSelectAllNotifications(checked: boolean) {
    if (checked) {
      setIsSelectingMultiple(true);
      setSelectedNotificationIds(filteredNotifications.map((notification) => notification.id));
      return;
    }
    setIsSelectingMultiple(false);
    setSelectedNotificationIds([]);
  }

  function handleCheckNotification(notificationId: string, checked: boolean) {
    setSelectedNotificationIds((notificationIds) => {
      if (checked) {
        return [...notificationIds, notificationId];
      }
      return notificationIds.filter((id) => id !== notificationId);
    });
  }

  function handleFlagNotification(notificationId: string, isFlagged: boolean) {
    const notification = notifications?.find((notification) => notification.id === notificationId);
    if (notification != null) {
      updateNotification({
        params: {notificationId},
        request: {...notification, isFlagged: !isFlagged},
      });
    }
  }

  function handleDeleteNotification(notificationId: string) {
    deleteNotification({
      params: {notificationId},
    });
  }

  function handleDeleteSelectedNotifications() {
    deleteNotifications(
      {request: selectedNotificationIds},
      {onSuccess: () => setIsSelectingMultiple(false)},
    );
  }

  const isLoading =
    isLoadingNotifications || isLoadingDeleteNotifications || isLoadingDeleteNotification;

  return (
    <Box
      sx={{
        display: 'flex',
        marginTop: {xs: '16px', md: '24px'},
        marginLeft: {xs: '60px', sm: '120px', md: '32px'},
      }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          width: {xs: 'calc(100vw - 120px)', sm: 'calc(100vw - 240px)', md: 'calc(100vw - 445px)'},
        }}>
        <Menu
          direction="row"
          showUnselectedText
          items={[
            {
              text:
                totals.unseenWarnings > 0
                  ? `${tabs[0].label} (${totals.unseenWarnings})`
                  : tabs[0].label,
              isSelected: selectedTabIndex === 0,
              onSelectItem: () => handleChangeSelectedTab(0),
            },
            {
              text:
                totals.unseenMaintenance > 0
                  ? `${tabs[1].label} (${totals.unseenMaintenance})`
                  : tabs[1].label,
              isSelected: selectedTabIndex === 1,
              onSelectItem: () => handleChangeSelectedTab(1),
            },
            {
              text: totals.flagged > 0 ? `${tabs[2].label} (${totals.flagged})` : tabs[2].label,
              isSelected: selectedTabIndex === 2,
              onSelectItem: () => handleChangeSelectedTab(2),
            },
          ]}
        />
        {selectedNotification != null ? (
          <Fragment>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: '40px',
                paddingBlock: '12px',
                paddingInline: '32px',
                backgroundColor: 'custom.surfaceQuaternary',
                borderRadius: '360px',
              }}>
              <Typography
                sx={{fontSize: '16px', fontStyle: 'normal', fontWeight: 500, lineHeight: 'normal'}}>
                <Span>{selectedNotification.ovenDescription}</Span>
                <Span sx={{marginInline: '5px'}}>|</Span>
                <Span sx={{fontWeight: 400}}>{selectedNotification.bakeryName}</Span>
              </Typography>
              <IconButton
                IconComponent={DeclineIcon}
                activeColor="custom.iconsPrimary"
                onClick={() => handleChangeSelectedTab(selectedTabIndex)}
              />
            </Box>
            <Box sx={{marginTop: '16px', paddingInline: '32px'}}>
              <Typography
                sx={{
                  fontSize: '20px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '28px',
                  whiteSpace: 'pre-line',
                }}>
                {stringUtils.format(
                  t(notificationUtils.getLabel(selectedNotification)),
                  selectedNotification.ovenPanelOrder ?? 1,
                )}
              </Typography>
            </Box>
          </Fragment>
        ) : (
          <Fragment>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: '40px',
                paddingBlock: '12px',
                paddingInline: '32px',
                backgroundColor: 'custom.surfaceSecondary',
                borderRadius: '360px',
              }}>
              <Typography
                sx={{
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: 'normal',
                  color: 'custom.textSecondary',
                }}>
                {t('profile_notifications_select_all_label')}
              </Typography>
              <SwitchInverted
                checked={isSelectingMultiple}
                onChange={(_, checked) => handleSelectAllNotifications(checked)}
              />
            </Box>
            <NotificationList
              notifications={filteredNotifications}
              onSelectNotification={handleSelectNotification}
              onCheckNotification={handleCheckNotification}
              onDeleteNotification={handleDeleteNotification}
              isSelectingMultiple={isSelectingMultiple}
              selectedNotificationIds={selectedNotificationIds}
              notificationsNotFoundLabel={
                isLoading ? '' : t('profile_notifications_not_found_label')
              }
            />
            <Box sx={{minHeight: '48px'}} />
          </Fragment>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          width: {xs: '60px', sm: '120px'},
          paddingTop: '56px',
        }}>
        {isSelectingMultiple && (
          <Box sx={{display: 'flex', alignItems: 'center', height: '40px'}}>
            <IconButton IconComponent={DeleteIcon} onClick={handleDeleteSelectedNotifications} />
          </Box>
        )}
        {selectedNotification != null && (
          <Box>
            <Box sx={{display: 'flex', alignItems: 'center', height: '40px'}}>
              <IconButton
                IconComponent={DeleteIcon}
                onClick={() => handleDeleteNotification(selectedNotification.id)}
              />
            </Box>
            <Box sx={{display: 'flex', alignItems: 'center', height: '40px'}}>
              <IconButton
                IconComponent={selectedNotification.isFlagged ? FilledFlagIcon : FlagIcon}
                onClick={() =>
                  handleFlagNotification(
                    selectedNotification.id,
                    Boolean(selectedNotification.isFlagged),
                  )
                }
              />
            </Box>
          </Box>
        )}
      </Box>
      <LoadingBackdrop isLoading={isLoading} />
    </Box>
  );
}

export default Notifications;
